import { useState, useEffect, useCallback } from 'react';
import type { ImageDto } from '@/components/shared/types';
import { getCostImages, deleteCostImage } from '../api/costImages';

interface UseCostImagesResult {
  images: ImageDto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deleteImage: (imageId: number) => Promise<void>;
}

/**
 * Hook to manage cost images
 * - Fetches images for a specific job cost
 * - Provides delete functionality
 * - Handles loading and error states
 */
export function useCostImages(jobId: number, costId: number): UseCostImagesResult {
  const [images, setImages] = useState<ImageDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getCostImages(jobId, costId);
      setImages(data);
    } catch (err) {
      setError('Failed to load images');
      console.error('Error fetching cost images:', err);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, costId]);

  const deleteImage = async (imageId: number) => {
    try {
      await deleteCostImage(jobId, costId, imageId);
      // Remove from local state
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  return {
    images,
    isLoading,
    error,
    refetch: fetchImages,
    deleteImage,
  };
}
